"use client";
import { cn } from "@/lib/utils";
import { BoCheckCircle } from "solar-icon-react/bo";
import Image from "next/image";
import Team1Image from "@public/team1.webp";
import Team2Image from "@public/team2.webp";
import AppContainer from "../appContainer";
import { FlexCenter, FlexCol } from "../ui";
import { useTranslations } from "next-intl";

interface FeatureItemProps {
  title: string;
  description: string;
}

const FeatureItem = ({ title, description }: FeatureItemProps) => {
  const t = useTranslations();
  return (
    <li className={cn("mb-4 flex items-start gap-2 lg:mb-0")}>
      <div className={cn("flex h-[50%] overflow-hidden")}>
        <BoCheckCircle size={30} className="text-primary-main!" />
      </div>
      <FlexCol gap={2} className="lg:gap-1">
        <h2 className="text-sm font-bold lg:text-base xl:text-lg">
          {t(title as any)}
        </h2>
        <p className="text-custom-gray-200 text-xs lg:text-sm xl:text-base">
          {t(description as any)}
        </p>
      </FlexCol>
    </li>
  );
};
const features: FeatureItemProps[] = [
  {
    title: "moreAboutUsSection.features.1.title",
    description: "moreAboutUsSection.features.1.desc",
  },
  {
    title: "moreAboutUsSection.features.2.title",
    description: "moreAboutUsSection.features.2.desc",
  },
  {
    title: "moreAboutUsSection.features.3.title",
    description: "moreAboutUsSection.features.3.desc",
  },
  {
    title: "moreAboutUsSection.features.4.title",
    description: "moreAboutUsSection.features.4.desc",
  },
];

export default function MoreAboutUsSection() {
  const t = useTranslations();

  return (
    <section className={cn("mb-20 w-full sm:mb-40 lg:mb-60")}>
      <AppContainer className={cn("h-full")}>
        <div className="mx-auto w-full max-w-[700px] lg:max-w-[900] xl:max-w-full">
          <FlexCenter
            className={cn(
              "mx-auto w-full flex-col-reverse gap-20 sm:flex-row lg:gap-6 xl:gap-20",
            )}
          >
            <article
              className={cn("flex w-full max-w-lg flex-col gap-4 xl:gap-5")}
            >
              <h3
                className={cn(
                  "text-primary-main hidden text-base font-bold sm:block lg:text-lg xl:text-2xl",
                )}
              >
                {t("moreAboutUsSection.title")}
              </h3>
              <p
                className={cn(
                  "mb-4 text-center text-sm leading-7 sm:text-justify xl:text-base",
                )}
              >
                {t("moreAboutUsSection.description")}
              </p>
              <ul className="flex flex-col gap-4 xl:gap-5">
                {features.map((feature, index) => (
                  <FeatureItem
                    key={index}
                    title={feature.title}
                    description={feature.description}
                  />
                ))}
              </ul>
            </article>
            <div className={cn("flex justify-center gap-6")}>
              <FlexCol className="sm:mt-20">
                <h3
                  className={cn(
                    "text-primary-main mb-4 text-center text-base font-bold sm:hidden",
                  )}
                >
                  {t("moreAboutUsSection.title")}
                </h3>
                <figure
                  className={cn(
                    "from-primary-main/100 to-success-main/0 mx-auto bg-gradient-to-b",
                    "h-[376px] w-[230px] overflow-hidden rounded-[15px] p-[2px]",
                  )}
                >
                  <Image
                    alt="team-image1"
                    title="team-image1"
                    className={cn("size-full rounded-[14px] object-cover")}
                    src={Team1Image}
                  />
                </figure>
              </FlexCol>
              <figure
                className={cn(
                  "from-primary-main/100 hidden bg-gradient-to-b to-white lg:flex",
                  "mb-20 h-[310px] w-[192px] items-center justify-center overflow-hidden rounded-[15px] p-[2px] lg:h-[376px] lg:w-[230px]",
                )}
              >
                <Image
                  alt="team-image1"
                  title="team-image1"
                  className={cn("size-full rounded-[14px] object-cover")}
                  src={Team2Image}
                />
              </figure>
            </div>
          </FlexCenter>
        </div>
      </AppContainer>
    </section>
  );
}
